import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import flagStyles from "../pages/admin/AdminFeatureFlags.module.css";

type Props = {
  onClose: () => void;
  titleId: string;
  children: ReactNode;
  // "md" (default) is the wide admin dialog; "sm" is a narrow focused dialog
  // for single-field flows like inviting a teammate.
  size?: "sm" | "md";
  // Optional CSS width override for the dialog (e.g. "min(480px, 92vw)"), for the
  // odd case where a `size` preset is a touch too narrow for its copy. Applied
  // inline so it wins over the data-size width without affecting other modals.
  width?: string;
  // "compact" sets the dialog's text, fields and buttons at the admin table
  // scale (13px body). Opt-in, so customer dialogs keep their own sizing.
  density?: "default" | "compact";
};

// Where the backdrop is portalled to. The dashboard declares one on its
// `.fontScope` wrapper, because the modal chrome is styled with the `--ds-*`
// tokens and mono font defined there - portalling all the way to <body> would
// strip a dashboard dialog of its typeface. Admin pages have no host and fall
// back to <body>, which is where they already looked.
const modalHost = (): HTMLElement =>
  document.querySelector<HTMLElement>("[data-modal-host]") ?? document.body;

// Ref-counted so a flow that swaps one ModalShell for another (the AI settings
// menu handing off to the ChatGPT switch) can't unlock the page mid-handover,
// and so React's dev-mode double-invoked effects balance out.
let scrollLocks = 0;
let savedOverflow = "";
let savedPaddingRight = "";

const lockPageScroll = (): void => {
  if (scrollLocks === 0) {
    // Classic scrollbars vanish with the lock and the page jumps sideways;
    // overlay scrollbars (the mac default) measure 0 and cost nothing.
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    savedOverflow = document.body.style.overflow;
    savedPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    if (gutter > 0) document.body.style.paddingRight = `${gutter}px`;
  }
  scrollLocks += 1;
};

const unlockPageScroll = (): void => {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) {
    document.body.style.overflow = savedOverflow;
    document.body.style.paddingRight = savedPaddingRight;
  }
};

export const ModalShell = ({
  onClose,
  titleId,
  children,
  size = "md",
  width,
  density = "default",
}: Props): JSX.Element => {
  // Resolved once: re-pointing a portal at a new container remounts its
  // children, which would reset a multi-step dialog to step one.
  const [host] = useState<HTMLElement>(modalHost);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // The page behind a modal must not scroll: the backdrop is viewport-sized, so
  // scrolling only ever slides unreachable content past a dialog that stays put.
  useEffect(() => {
    lockPageScroll();
    return unlockPageScroll;
  }, []);

  const onBackdropClick = (e: MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) onClose();
  };

  const onDialogClick = (e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
  };

  // Portalled deliberately. Rendered in place, a backdrop inherits every
  // ancestor stacking context - `.hero` sets `isolation: isolate`, which pinned
  // the whole modal under the page sections below it.
  return createPortal(
    <div
      className={flagStyles.dialogBackdrop}
      data-modal-backdrop=""
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onBackdropClick}
    >
      <div
        className={flagStyles.dialog}
        data-size={size}
        data-density={density}
        onClick={onDialogClick}
        {...(width !== undefined ? { style: { width } } : {})}
      >
        {children}
      </div>
    </div>,
    host,
  );
};
